import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import pic from "assets/landing/deal_flow_kit.png";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { getParams, getSource, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";
import { useSubscription } from "utils/useSubscription";

import ProBundleAuth from "./Auth";
import ProgramInfo from "./ProgramInfo";

const ProPartnershipBundle = () => {
  const location = useLocation();
  const isSignedIn = getParams("signed_in") === "true";
  const navigate = useNavigate();
  const source = getSource();
  const { setAlert } = useContext(AlertContext);

  const { isLoaded } = useAuth();
  const { fetchUserInfo, fetchUserLoading } = useLogin();

  const { currentOrg } = useContext(OrganizationUserContext);
  const { subscription, isProPlan } = useContext(SubscriptionContext);
  const { handleCheckout } = useSubscription();

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const successCheckout = getParams("checkout") === "success";

  const notQualified =
    (isProPlan && subscription?.isSubscribed) ||
    subscription?.partnershipData?.status === SubscriptionType.ACTIVE;

  const checkoutProBundle = async () => {
    if (notQualified) {
      setAlert(
        "You already subscribed to one of the plans in this bundle. Please go to Settings page to manage your subscription.",
        "info",
      );
      navigate(`/${routes.home}${location.search}`, { replace: true });
    } else {
      handleCheckout(
        Plans.PRO,
        source || "",
        window.location.pathname,
        SplashPage.pro_bundle,
      );
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && currentOrg?.id) {
      checkoutProBundle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id, subscription?.subscription?.planName]);

  useEffect(() => {
    if (successCheckout) {
      navigate(`/${routes.dashboard}${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCheckout]);

  return fetchUserLoading || !isLoaded ? (
    <Backdrop
      sx={{
        backgroundColor: "white",
        zIndex: 9999,
        position: "absolute",
      }}
      open={fetchUserLoading}
      appear={false}
    >
      <CircularProgress />
    </Backdrop>
  ) : (
    <Grid
      container
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        justifyContent: "center",
      }}
    >
      <Grid container sx={{ maxWidth: 1600 }}>
        {!isMobileScreen && (
          <Grid
            item
            xs={6}
            sx={{
              backgroundImage: `url(${pic})`,
              backgroundSize: "contain",
              backgroundPosition: "bottom",
              backgroundRepeat: "no-repeat",
            }}
          ></Grid>
        )}

        <Grid
          item
          xs
          md
          lg
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Routes>
            <Route
              path="/"
              element={
                <ProgramInfo
                  checkoutProBundle={checkoutProBundle}
                  fetchUserLoading={fetchUserLoading || !isLoaded}
                  notQualified={notQualified}
                  isMobileScreen={isMobileScreen}
                />
              }
            />
            <Route
              path="/sign-in/*"
              element={<ProBundleAuth action={AuthAction.SIGN_IN} />}
            />
            <Route
              path="/sign-up/*"
              element={<ProBundleAuth action={AuthAction.SIGN_UP} />}
            />
          </Routes>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProPartnershipBundle;
